/**
 * 67.50 (1) (the owner: "pre placed objects get eaten by fine terrain"): the drawn fine relief flattened under the wide
 * props. A prop stands on the coarse ground in the sim (core/gameState.ts, core/hits.ts) and now drawn
 * (rendering/groundSeat.ts); the drawn fine ground (rendering/fineTerrainPatch.ts) and the ground scatter
 * (rendering/groundScatter.ts) take the relief's lift × `flatFactor`, so no hummock rises through a rock's base or a
 * plinth. A disc: a rock's half width, a structure's or a ruin's shape reach (its box's corners within).
 * The cut (the coordinator, Oct 11: the flatten took 10 % of the ground round the fight, with 0.6 people of blend round
 * every prop, trees included): only rocks, structures and ruins, each on its own base disc with no blend
 * (`FINE_FLATTEN.marginPeople` 0) — the prop covers its disc, so the flattening is not seen. A tree's trunk enters the
 * relief (its foot under the hummocks, as a trunk in rough ground); its grass tufts take the relief at their spot
 * (rendering/treeStands.ts). Presentation only.
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { planetObstacles } from '../core/surfaceWalk';
import { planetShapes } from '../core/hits';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { footingOf } from '../core/stands';

export const FINE_FLATTEN = { marginPeople: 0 };

/**
 * The props whose bases flatten the relief: the structures and the ruins (71.5 brings their footings into the one ground).
 * 71.2: not the rocks — a rock stands on the fine ground itself now (core/propSeat.ts), sim and drawing alike, so there is
 * nothing under it to flatten; a tree's trunk enters the relief as before.
 */
const FLATTENED = new Set(['structure', 'ruin']);

/**
 * A prop's flattened disc: its ground direction (unit) and its radius (world units, along the ground); 67.52: and the height
 * over the coarse ground it is flattened to — a seated build's base (StructureState.baseLift), else 0 (the coarse ground).
 */
export interface FlatDisc { dir: Vec3; r: number; lift?: number }

const memo = new WeakMap<object, { planetIndex: number; shapes: unknown; discs: FlatDisc[] }>();

/** The props' discs on a planet — the same array while its walk obstacles and shapes are the same objects. */
export function flatDiscs(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets' | 'config'> & Partial<Pick<GameState, 'ruins'>>, planetIndex: number): FlatDisc[] {
  const po = planetObstacles(state, planetIndex), shapes = planetShapes(state, planetIndex);
  const m = memo.get(po);
  if (m && m.planetIndex === planetIndex && m.shapes === shapes) return m.discs;
  const reach = new Map<string, number>(), lifts = new Map<number, number>();
  for (const s of state.structures) if (s.planetIndex === planetIndex && s.baseLift) lifts.set(s.id, s.baseLift);
  for (const sh of shapes.list) if (sh.ref.kind === 'structure' || sh.ref.kind === 'ruin') reach.set(`${sh.ref.kind}:${sh.ref.id}`, sh.reach);
  // (209's follow-up: a walkable top files no disc of its hit box's bound — 6 people round the pad, flattened to its base, the
  // drawn ground stood up to a person over the sim's under its skirt; its footprint's own disc is below, the skirt drawn over
  // the ground beyond, rendering/standSkirts.ts)
  const tops = new Set<number>();
  for (const s of state.structures) if (s.planetIndex === planetIndex && footingOf(s, state.config) && s.baseLift !== undefined) tops.add(s.id);   // (lane F's footing follow-up: the buildings' plinths too)
  const discs: FlatDisc[] = [];
  for (const o of po.list) {
    if (!o.ref || !FLATTENED.has(o.ref.kind)) continue;
    if (o.ref.kind === 'structure' && tops.has(o.ref.id)) continue;
    const r = o.ref.kind === 'structure' || o.ref.kind === 'ruin' ? reach.get(`${o.ref.kind}:${o.ref.id}`) ?? o.radius : o.radius;
    const l = Math.hypot(o.dir.x, o.dir.y, o.dir.z) || 1;
    const lift = o.ref.kind === 'structure' ? lifts.get(o.ref.id) : undefined;
    discs.push({ dir: { x: o.dir.x / l, y: o.dir.y / l, z: o.dir.z / l }, r, ...(lift ? { lift } : {}) });
  }
  // 209's follow-up (the owner: the land blended over the pad's edge "looks bad"): a walkable top's footprint flattened to the
  // coarse ground, under its deck (which stands on the highest ground under it: core/buildSeat.ts) — its inscribed disc
  const person = PERSON_BODY_HEIGHT * personSetOf(state as GameState).k;
  for (const s of state.structures) {
    const top = footingOf(s, state.config);   // (209's footing follow-up: the buildings' plinths too)
    if (!top || s.planetIndex !== planetIndex || s.baseLift === undefined) continue;
    const l = Math.hypot(s.surfaceDir.x, s.surfaceDir.y, s.surfaceDir.z) || 1;
    discs.push({ dir: { x: s.surfaceDir.x / l, y: s.surfaceDir.y / l, z: s.surfaceDir.z / l }, r: Math.min(top.halfXPeople, top.halfZPeople) * person });
  }
  memo.set(po, { planetIndex, shapes, discs });
  return discs;
}

/** The share of the relief kept at a point `d` world units from a disc of radius `r`: 0 inside, 1 a margin past it (past it, with no margin). */
export function flatWeight(d: number, r: number, margin: number): number {
  if (!(margin > 0)) return d > r ? 1 : 0;
  const t = Math.min(1, Math.max(0, (d - r) / margin));
  return t * t * (3 - 2 * t);
}

/** The relief's share kept at `dir` on `planet` by every disc (the least of their weights); 1 with none near. */
export function flatFactor(planet: PlanetState, dir: Vec3, discs: readonly FlatDisc[], person: number): number {
  const margin = FINE_FLATTEN.marginPeople * person, R = planet.radius;
  const l = Math.hypot(dir.x, dir.y, dir.z) || 1;
  let f = 1;
  for (const q of discs) {
    const dx = dir.x / l - q.dir.x, dy = dir.y / l - q.dir.y, dz = dir.z / l - q.dir.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz) * R;
    if (d >= q.r + margin) continue;
    f = Math.min(f, flatWeight(d, q.r, margin));
    if (f === 0) break;
  }
  return f;
}

/** The person's height in world units for this state (the margin's ruler). */
export const personOf = (state: Pick<GameState, 'config'>) => PERSON_BODY_HEIGHT * personSetOf(state as GameState).k;
